import React from "react";
import tw from "twin.macro";
import { getResearchUrl } from "./helpers/utmHelper";

const PageWrapper = tw.div`max-w-5xl mx-auto px-4 py-12`;
const HeroSection = tw.div`text-center mb-16`;
const PageTitle = tw.h1`text-3xl md:text-5xl font-bold text-secondary-500 mb-4`;
const Subtitle = tw.p`text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed`;

const Section = tw.div`mb-12`;
const SectionTitle = tw.h2`text-2xl md:text-3xl font-bold text-secondary-500 mb-4`;
const SectionText = tw.p`text-gray-600 text-lg leading-relaxed mb-4`;

const CardGrid = tw.div`grid grid-cols-1 md:grid-cols-2 gap-6 mt-8`;
const Card = tw.div`rounded-xl shadow-lg p-6 bg-white border border-gray-100`;
const CardTitle = tw.h3`text-xl font-bold text-primary-500 mb-2`;
const CardText = tw.p`text-gray-600 leading-relaxed`;

const CTASection = tw.div`text-center mt-16 py-12 bg-green-500 rounded-2xl`;
const CTATitle = tw.h2`text-2xl md:text-3xl font-bold text-secondary-500 mb-4`;
const CTASubtitle = tw.p`text-gray-600 mb-8 text-lg`;
const CTAButton = tw.a`px-10 py-4 bg-blue-500 text-white no-underline hover:bg-primary-500 font-semibold inline-block rounded-xl shadow-lg tracking-wide uppercase text-sm transition-all duration-300 ease-in-out`;

const ResearchPage = ({ isMobile }) => {
  return (
    <PageWrapper style={{ marginTop: isMobile ? "4rem" : "5rem" }}>
      <HeroSection>
        <PageTitle>Uclusion for researchers</PageTitle>
        <Subtitle>
          AI turned research into code work. Run your analyses, simulations, and
          pipelines the way the best solo developers run theirs &mdash; with
          hypotheses, evidence, and decisions in one place that you and your AI
          agents both read.
        </Subtitle>
      </HeroSection>

      <Section>
        <SectionTitle>Your research is code now</SectionTitle>
        <SectionText>
          With AI agents writing your analysis scripts and data pipelines, you
          have the same coordination problems developers have: what should the
          agent work on next, what did we already decide, and who approved this
          result. One command connects Claude Code, Cursor, or Codex to your
          workspace. Your agent asks for work, asks questions when it is unsure,
          and reports back &mdash; instead of burying everything in a scrolling
          chat.
        </SectionText>
      </Section>

      <Section>
        <SectionTitle>Built for how research actually moves</SectionTitle>
        <CardGrid>
          <Card>
            <CardTitle>Hypotheses are votable options</CardTitle>
            <CardText>
              A hypothesis is an option on a question. Evidence accumulates as
              comments and certainty votes on each option, and when you decide,
              the rationale &mdash; including the options you rejected &mdash;
              stays on the record for the next session, the next agent, or the
              you of six months from now.
            </CardText>
          </Card>
          <Card>
            <CardTitle>Questions that survive months</CardTitle>
            <CardText>
              Research questions don&rsquo;t resolve in a sprint. Uclusion
              questions stay open as long as they need to, keep their full
              history of options and votes, and never evaporate into chat
              scrollback or a cleared AI context.
            </CardText>
          </Card>
          <Card>
            <CardTitle>AI output earns its place</CardTitle>
            <CardText>
              Nothing an agent produces has to enter your record unreviewed.
              Agents approve with a certainty score and a written reason, ask
              before acting on ambiguity, and open a review when results are
              ready &mdash; so every claim in your workspace has provenance.
            </CardText>
          </Card>
          <Card>
            <CardTitle>A lab notebook that runs itself</CardTitle>
            <CardText>
              Notes, decisions, progress reports, and resolved questions
              accumulate on the work as it happens. Generate a Markdown report
              from the CLI to hand your advisor or collaborators &mdash; no
              account required to read it.
            </CardText>
          </Card>
        </CardGrid>
      </Section>

      <Section>
        <SectionTitle>Keep your stack</SectionTitle>
        <SectionText>
          Uclusion is not another notes tool. Zotero, Obsidian, and Overleaf
          stay exactly where they are &mdash; Uclusion sits above them as the
          coordination layer, holding the open questions, the evidence votes,
          and the decisions. And the CLI can export your entire workspace to
          Markdown, so your record is never locked in.
        </SectionText>
      </Section>

      <CTASection>
        <CTATitle>Ready to run your research with Uclusion?</CTATitle>
        <CTASubtitle>
          Free for a single user &mdash; and your first collaborator is an AI.
        </CTASubtitle>
        <CTAButton href={getResearchUrl()}>Start free</CTAButton>
      </CTASection>
    </PageWrapper>
  );
};

export default ResearchPage;
